import React, { useState } from 'react';
import type { ReportTypeId } from '../../config/reportTypes';
import {
  computeReadinessGaps,
  groupGapsByCategory,
  type ReadinessGap,
  type ReadinessGapAction,
  type ReadinessGapInputBase,
} from '../../lib/coverage/readinessGaps';
import { CELL_DOT_CLASS, CELL_LABEL } from './coverage/coverageMatrixUi';

export interface ReportReadinessPanelProps {
  reportTypeId: ReportTypeId;
  reportTypeLabel: string;
  /**
   * Full inputs for the tier-aware gap re-resolution (J8). The host
   * passes the site's persisted coverage, SED, intent, prefs, docs +
   * whether a report of this type exists; the panel re-resolves the
   * setup/intent rows against ACTUAL state so they no longer show a
   * false "Missing".
   */
  gapInput: ReadinessGapInputBase & { hasGeneratedReport: boolean };
  onClose: () => void;
  /** Routes a gap's resolution action onto the host's existing
   *  affordances (intent form / wizard / doc library / re-run). */
  onResolveGap?: (action: ReadinessGapAction) => void;
  onOpenCoverageMatrix?: () => void;
}

/** Per-category action-button label. */
function actionLabel(action: ReadinessGapAction): string {
  switch (action.kind) {
    case 'fill-intent': return 'Confirm →';
    case 'open-setup':  return 'Set up →';
    case 'upload-doc':  return 'Upload →';
    case 'rerun':       return 'Re-run →';
  }
}

export function ReportReadinessPanel({
  reportTypeId,
  reportTypeLabel,
  gapInput,
  onClose,
  onResolveGap,
  onOpenCoverageMatrix,
}: ReportReadinessPanelProps) {
  const { total, ready, gaps } = computeReadinessGaps({ reportTypeId, ...gapInput });
  const grouped = groupGapsByCategory(gaps);
  const progressPercent = total > 0 ? (ready / total) * 100 : 0;

  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="flex flex-col bg-background h-full text-foreground">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border-subtle">
        <h2 className="type-body font-bold">{reportTypeLabel} Readiness</h2>
        <button
          onClick={onClose}
          className="text-text-muted hover:text-text-primary p-1"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <div className="p-4 overflow-y-auto space-y-6">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between type-meta font-medium">
            <span>{ready} / {total} requirements ready</span>
          </div>
          <div className="h-2 w-full bg-surface-3 rounded-full overflow-hidden">
            <div
              className="h-full bg-success transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {gaps.length === 0 && (
          <p className="type-body text-text-secondary">
            No outstanding requirements — every input this report relies on is in place.
          </p>
        )}

        {/* One section per resolution category, each with an accurate
            header + sub-label (replaces the confusing blanket
            "System-fetched, not yet available"). */}
        {grouped.map((section) => (
          <section key={section.category} className="space-y-3">
            <div>
              <h3 className="type-meta font-bold uppercase tracking-widest text-text-secondary">
                {section.header}
              </h3>
              <p className="type-meta text-text-muted">{section.subLabel}</p>
            </div>
            <div className="space-y-2">
              {section.gaps.map((gap) => (
                <GapRow
                  key={gap.id}
                  gap={gap}
                  expanded={expanded === gap.id}
                  onToggle={() =>
                    setExpanded((cur) => (cur === gap.id ? null : gap.id))
                  }
                  onResolve={onResolveGap}
                />
              ))}
            </div>
          </section>
        ))}

        {onOpenCoverageMatrix && (
          <div className="pt-6">
            <button
              onClick={onOpenCoverageMatrix}
              className="w-full py-2 px-4 border border-border hover:bg-state-hover rounded text-sm transition-colors"
            >
              View Full Coverage Matrix
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GapRow({
  gap,
  expanded,
  onToggle,
  onResolve,
}: {
  gap: ReadinessGap;
  expanded: boolean;
  onToggle: () => void;
  onResolve?: (action: ReadinessGapAction) => void;
}) {
  // User-actionable gaps (intent / setup / upload) get the warning
  // treatment; system-fetched gaps are neutral (no user action blocks
  // them — they fill on the next run).
  const userActionable = gap.category !== 'system-data';
  const containerClass = userActionable
    ? 'border-warning/30 bg-warning/5'
    : 'border-border-subtle';

  return (
    <div className={`border rounded px-3 py-2 ${containerClass}`}>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 min-w-0 text-left"
          aria-expanded={expanded}
        >
          <span
            className={`h-2 w-2 rounded-full shrink-0 ${CELL_DOT_CLASS[gap.status]}`}
          />
          <span className="type-body truncate">{gap.label}</span>
          <span className="type-meta text-text-muted shrink-0">
            {expanded ? '▾' : '▸'}
          </span>
        </button>
        {onResolve ? (
          <button
            onClick={() => onResolve(gap.action)}
            className="type-meta font-medium text-primary hover:text-primary-hover px-2 py-1 rounded bg-primary/10 hover:bg-primary/20 transition-colors shrink-0"
          >
            {actionLabel(gap.action)}
          </button>
        ) : (
          <span className="type-meta bg-surface-3 text-text-secondary px-2 py-0.5 rounded shrink-0">
            {CELL_LABEL[gap.status]}
          </span>
        )}
      </div>
      {expanded && (
        <div className="mt-2 pl-4 space-y-1">
          <p className="type-meta text-text-secondary">{gap.meaning}</p>
          <p className="type-meta text-text-muted">
            <span className="font-medium">How to resolve:</span> {gap.resolution}
          </p>
        </div>
      )}
    </div>
  );
}
